import type { ComponentProps } from '@enonic/react-components'
import type { ResponsiveImageData } from '../primitives'
import type { BreadcrumbItem } from '../primitives'
import type { CtaButton } from '../../utils/ctaUtils'
import * as React from 'react'
import { ResponsiveImage, Container, ButtonLink } from '../primitives'
import { Icon } from '/react4xp/components/primitives/Icon'

interface MetadataItem {
  label: string
  value: string
}

export interface EducationData extends Record<string, unknown> {
  title: string
  lead?: string
  heroImage?: ResponsiveImageData
  ctaPrimary?: CtaButton
  ctaSecondary?: CtaButton
  location?: string
  duration?: string
  credits?: string
  startDate?: string
  educationForm?: string
}

export const Education = (props: ComponentProps) => {
  const data = props.data as EducationData
  const commonData = props.common as Record<string, unknown> | undefined
  const breadcrumbs = commonData?.breadcrumbs as BreadcrumbItem[] | undefined
  const {
    title,
    lead,
    heroImage,
    ctaPrimary,
    location,
    duration,
    credits,
    startDate,
    educationForm,
  } = data

  const metadataItems: MetadataItem[] = []
  if (duration)
    metadataItems[metadataItems.length] = { label: 'Varighet', value: duration }
  if (credits)
    metadataItems[metadataItems.length] = {
      label: 'Studiepoeng',
      value: credits,
    }
  if (startDate)
    metadataItems[metadataItems.length] = {
      label: 'Studiestart',
      value: startDate,
    }
  if (educationForm)
    metadataItems[metadataItems.length] = {
      label: 'Utdanningsform',
      value: educationForm,
    }

  return (
    <>
      {/* Education hero — blue panel variant */}
      <section
        data-hero
        className="relative overflow-hidden md:min-h-[max(700px,70svh)]"
      >
        {heroImage && (
          <div aria-hidden="true" className="relative md:absolute md:inset-0">
            <ResponsiveImage
              data={heroImage}
              className="aspect-4/3 h-full w-full object-cover md:aspect-auto"
              fetchpriority="high"
            />
            <div className="absolute inset-0 hidden bg-black/25 mix-blend-multiply md:block" />
            <div className="absolute inset-0 hidden bg-linear-to-b from-transparent to-black/50 mix-blend-multiply md:block" />
          </div>
        )}

        <div className="relative md:flex md:min-h-[inherit] md:items-end">
          <div className="relative w-full">
            <div
              aria-hidden="true"
              className="absolute inset-y-0 left-0 w-full bg-blue-900 md:w-4/5"
            />
            <div className="relative mx-auto w-full max-w-container px-5 pb-16 pt-8 md:px-16">
              <div className="flex flex-col gap-8 md:max-w-[60%]">
                  {breadcrumbs && breadcrumbs.length > 1 && (
                    <nav aria-label="Brødsmuler">
                      {/* Mobile: single back link to parent */}
                      <a
                        href={breadcrumbs[breadcrumbs.length - 2].url}
                        className="inline-flex items-center gap-1 text-base leading-snug tracking-wide text-slate-300 no-underline hover:underline focus-visible:outline-1 focus-visible:outline-slate-300 md:hidden"
                      >
                        <span aria-hidden="true">←</span>
                        {breadcrumbs[breadcrumbs.length - 2].title}
                      </a>
                      {/* Desktop: full breadcrumb */}
                      <ol className="hidden items-center gap-2 text-lg leading-snug tracking-wide md:flex">
                        {breadcrumbs.map((item, index) => {
                          const isLast = index === breadcrumbs.length - 1
                          return (
                            <li key={index} className="flex items-center gap-2">
                              {index > 0 && (
                                <span
                                  className="text-slate-300"
                                  aria-hidden="true"
                                >
                                  /
                                </span>
                              )}
                              {isLast ? (
                                <span
                                  className="text-white"
                                  aria-current="page"
                                >
                                  {item.title}
                                </span>
                              ) : (
                                <a
                                  href={item.url}
                                  className="text-slate-300 no-underline hover:underline focus-visible:outline-1 focus-visible:outline-slate-300"
                                >
                                  {item.title}
                                </a>
                              )}
                            </li>
                          )
                        })}
                      </ol>
                    </nav>
                  )}

                  <h1
                    className="font-serif text-4xl leading-tightest text-white hyphens-auto wrap-break-word md:text-5xl"
                    lang="nb"
                  >
                    {title}
                  </h1>

                  {(location || ctaPrimary) && (
                    <div className="flex flex-wrap items-center gap-8">
                      {location && (
                        <div className="flex w-full items-center gap-2 text-white">
                          <Icon
                            name="location_on"
                            size={28}
                            className="shrink-0"
                          />
                          <span className="text-base">{location}</span>
                        </div>
                      )}
                      {ctaPrimary && (
                        <ButtonLink
                          href={ctaPrimary.url}
                          label={ctaPrimary.label}
                          variant="button"
                        />
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {metadataItems.length > 0 && (
        <section className="bg-blue-300">
          <Container>
            <div className="col-span-full grid grid-cols-2 gap-x-6 gap-y-6 py-8 md:flex md:items-center md:justify-between md:py-10">
              {metadataItems.map((item, index) => (
                <React.Fragment key={item.label}>
                  {index > 0 && (
                    <span
                      className="hidden h-15 w-px bg-blue-900 md:block"
                      aria-hidden="true"
                    />
                  )}
                  <dl className="flex flex-col gap-2">
                    <dt className="text-base text-slate-900">{item.label}</dt>
                    <dd className="text-lg text-black">{item.value}</dd>
                  </dl>
                </React.Fragment>
              ))}
            </div>
          </Container>
        </section>
      )}

      {lead && (
        <section className="bg-white">
          <Container>
            <p className="col-span-full md:col-span-8 md:col-start-3 py-12 text-xl text-black md:py-16 md:text-3xl">
              {lead}
            </p>
          </Container>
        </section>
      )}
    </>
  )
}
